import { useEffect, useState, type FormEvent } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import { Button, DateTimeInput, OptionPicker, SpotPicker, useConfirm } from "@/ui";
import {
  useCreateTraining,
  useNotifyTrainingUpdate,
  useSpots,
  useTraining,
  useUpdateTraining,
  type TrainingLevel,
} from "@/features/queries";
import { noAutofill } from "@/lib/noAutofill";

interface Props {
  mode: "create" | "edit";
}

export function AdminTrainingFormPage({ mode }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const trainingId = mode === "edit" ? Number(params.id) : null;
  // When this page was opened from /events via a "+", return there instead
  // of dropping the user into the admin panel.
  const returnTo =
    (location.state as { from?: string } | null)?.from ?? "/admin/trainings";

  const { data: existing, isLoading } = useTraining(trainingId);
  const { data: spots = [] } = useSpots();
  const createMut = useCreateTraining();
  const updateMut = useUpdateTraining();
  const notifyMut = useNotifyTrainingUpdate();
  const confirm = useConfirm();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [spotId, setSpotId] = useState<number | null>(null);
  const [dateTime, setDateTime] = useState<string | null>(() => {
    if (mode !== "create") return null;
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(18, 0, 0, 0);
    return d.toISOString();
  });
  const [duration, setDuration] = useState("60");
  const [maxParticipants, setMaxParticipants] = useState("8");
  const [price, setPrice] = useState("0");
  const [level, setLevel] = useState<TrainingLevel>("all");

  useEffect(() => {
    if (mode !== "edit" || !existing) return;
    setTitle(existing.title);
    setDescription(existing.description ?? "");
    setSpotId(existing.spot_id);
    setDateTime(existing.date_time);
    setDuration(String(existing.duration));
    setMaxParticipants(String(existing.max_participants));
    setPrice(String(existing.price));
    setLevel(existing.level as TrainingLevel);
  }, [mode, existing]);

  if (mode === "edit" && (isLoading || !existing)) {
    return (
      <div>
        <BackLink onClick={() => navigate(returnTo)} />
        <p className="body">Загрузка…</p>
      </div>
    );
  }

  const mut = mode === "create" ? createMut : updateMut;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (spotId == null || !dateTime) return;
    const payload = {
      title: title.trim(),
      description: description.trim() || null,
      spot_id: spotId,
      date_time: dateTime,
      duration: Number(duration),
      max_participants: Number(maxParticipants),
      price: Number(price),
      level,
    };
    try {
      if (mode === "create") {
        await createMut.mutateAsync(payload);
      } else {
        await updateMut.mutateAsync({ id: trainingId!, patch: payload });
        const notify = await confirm({
          title: "Предупредить участников об изменениях?",
          description:
            "Всем записавшимся уйдёт уведомление в Telegram с обновлённой информацией о тренировке.",
          confirmLabel: "Да, отправить",
          cancelLabel: "Не надо",
        });
        if (notify) {
          try {
            await notifyMut.mutateAsync(trainingId!);
          } catch {
            /* notification failure doesn't block navigation */
          }
        }
      }
      navigate(returnTo);
    } catch {
      /* shown below */
    }
  };

  return (
    <>
      <BackLink onClick={() => navigate(returnTo)} />

      <h1 className="h1" style={{ marginBottom: 16 }}>
        {mode === "create" ? "Новая тренировка" : "Редактирование тренировки"}
      </h1>

      <form
        onSubmit={onSubmit}
        style={{
          background: "var(--bg-section)",
          borderRadius: "var(--r-lg)",
          padding: 24,
          maxWidth: 640,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <FormField label="Название">
          <TextInput value={title} onChange={setTitle} required />
        </FormField>

        <FormField label="Описание">
          <TextArea value={description} onChange={setDescription} rows={3} />
        </FormField>

        <FormField label="Площадка">
          <SpotPicker spots={spots} value={spotId} onChange={setSpotId} />
        </FormField>

        <FormField label="Дата и время">
          <DateTimeInput value={dateTime} onChange={setDateTime} required />
        </FormField>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
          <FormField label="Длительность, мин">
            <TextInput type="number" value={duration} onChange={setDuration} min={15} max={480} />
          </FormField>
          <FormField label="Мест">
            <TextInput type="number" value={maxParticipants} onChange={setMaxParticipants} min={1} max={100} />
          </FormField>
          <FormField label="Цена, ₽">
            <TextInput type="number" value={price} onChange={setPrice} min={0} />
          </FormField>
        </div>

        <FormField label="Уровень">
          <OptionPicker<TrainingLevel>
            value={level}
            onChange={setLevel}
            options={[
              { value: "all", label: "Для всех" },
              { value: "beginner", label: "Начинающий" },
              { value: "intermediate", label: "Средний" },
              { value: "advanced", label: "Продвинутый" },
            ]}
          />
        </FormField>

        {mut.error && (
          <div style={{
            background: "rgba(223,63,64,0.1)", color: "var(--fg-danger)",
            padding: "8px 12px", borderRadius: "var(--r-md)", fontSize: 13,
          }}>
            {(mut.error as Error).message}
          </div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <Button
            type="submit"
            disabled={mut.isPending || !title || spotId == null || !dateTime}
          >
            {mut.isPending
              ? "Сохраняем…"
              : mode === "create" ? "Создать" : "Сохранить"}
          </Button>
          <Button variant="ghost" onClick={() => navigate(returnTo)}>
            Отмена
          </Button>
        </div>
      </form>
    </>
  );
}

function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: "transparent", border: 0, color: "var(--fg2)", cursor: "pointer",
        display: "inline-flex", alignItems: "center", gap: 6, padding: 0, marginBottom: 12,
        fontFamily: "var(--font-body)", fontSize: 13,
      }}
    >
      <ArrowLeft size={14} /> назад
    </button>
  );
}

const inputStyle: React.CSSProperties = {
  border: "1px solid var(--separator)",
  borderRadius: "var(--r-md)",
  padding: "10px 14px",
  fontFamily: "var(--font-body)",
  fontSize: 15,
  background: "var(--bg)",
  color: "var(--fg1)",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

// <div>, not <label>: composite pickers (SpotPicker) render a trigger button
// plus a search input; a surrounding <label> delegates clicks to the first
// focusable child, which in Yandex/some Chromium variants swallows clicks
// on picker rows.
function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span className="label" style={{ color: "var(--fg2)" }}>{label}</span>
      {children}
    </div>
  );
}

function TextInput({
  value, onChange, type = "text", min, max, required,
}: {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  min?: number;
  max?: number;
  required?: boolean;
}) {
  return (
    <input
      type={type}
      value={value}
      min={min}
      max={max}
      required={required}
      onChange={(e) => onChange(e.target.value)}
      style={inputStyle}
      {...noAutofill}
    />
  );
}

function TextArea({
  value, onChange, rows = 3,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      rows={rows}
      onChange={(e) => onChange(e.target.value)}
      style={{ ...inputStyle, resize: "vertical", fontFamily: "var(--font-body)" }}
    />
  );
}
